// frontend/src/pages/ResetPassword.tsx

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid or missing reset link. Please request a new one.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setIsLoading(true);
      await api.post('/auth/reset-password', { token, password });
      toast.success('Password reset successfully. Please log in.');
      navigate('/login', { replace: true });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Reset link is invalid or expired. Please request a new one.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-apple-blue-50 to-white py-12 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-8 h-8 text-apple-blue-500" />
          <span className="text-2xl font-semibold">SLE DEX</span>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-3xl">Reset Password</CardTitle>
          </CardHeader>

          <CardContent>
            {!token ? (
              <div className="text-center space-y-4">
                <p className="text-apple-gray-700">
                  This reset link is invalid or incomplete. Please request a new one.
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-block text-apple-blue-500 hover:text-apple-blue-600 font-medium"
                >
                  Request a new link
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label="New password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />

                <Input
                  label="Confirm new password"
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />

                <Button type="submit" variant="primary" className="w-full" isLoading={isLoading}>
                  Reset password
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-apple-blue-500 hover:text-apple-blue-600"
                  >
                    Back to login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
